import type {
  MembershipDTO,
  OrganizationDTOV2,
  UserDtoV2,
} from '@workers-benefit-fund/api'

import type {
  CurrentSessionOrganization,
  IUserSessionStore,
  SessionMembership,
  SessionOrganization,
  SessionUser,
} from './user.types'

export interface AuthProfileResponse {
  user: UserDtoV2
  organization: OrganizationDTOV2
  membership: MembershipDTO
  policies?: string[]
}

export type SessionProfile = Pick<
  IUserSessionStore,
  'user' | 'membership' | 'policies'
> & { organization: CurrentSessionOrganization }

export type AuthSessionProfileResponse = Omit<
  AuthProfileResponse,
  'organization' | 'membership'
> & {
  organization: OrganizationDTOV2 | null
  membership: MembershipDTO | null
}

export type RestorableSessionProfile = Omit<
  SessionProfile,
  'organization' | 'membership'
> & {
  organization: CurrentSessionOrganization | null
  membership: SessionMembership | null
}

export const toSessionUser = (user: UserDtoV2): SessionUser => ({
  id: user.id,
  firstName: user.firstName,
  lastName: user.lastName,
  username: (user as UserDtoV2 & { username?: string }).username,
  email: user.email,
  phone: user.phone,
  aircallUserId: user.aircallUserId,
})

export const toSessionMembership = (
  membership: MembershipDTO,
): SessionMembership => ({
  id: membership.id ?? '',
  status: membership.status,
  membershipRole: membership.membershipRole?.id
    ? {
        id: membership.membershipRole.id,
        name: membership.membershipRole.name,
      }
    : undefined,
})

export const toSessionOrganization = (
  organization: OrganizationDTOV2,
): SessionOrganization => ({
  id: organization.id,
  name: organization.name,
  code: organization.code,
  memberships: organization.memberships?.map((membership) => ({
    id: membership.id ?? '',
  })),
})

export const toCurrentSessionOrganization = (
  organization: OrganizationDTOV2,
): CurrentSessionOrganization => ({
  id: organization.id,
  name: organization.name,
  code: organization.code,
  logoURL: organization.logoURL,
  circularLogoURL: organization.circularLogoURL,
  profile: organization.profile
    ? {
        isTLCRequired: organization.profile.isTLCRequired,
        leadCreationRequiredFields:
          organization.profile.leadCreationRequiredFields,
        frequentlyUsedLanguages: (
          organization.profile as { frequentlyUsedLanguages?: string[] }
        ).frequentlyUsedLanguages,
      }
    : undefined,
})

export const toSessionProfile = (
  profile: AuthProfileResponse,
): SessionProfile => ({
  user: toSessionUser(profile.user),
  organization: toCurrentSessionOrganization(profile.organization),
  membership: toSessionMembership(profile.membership),
  policies: profile.policies ?? [],
})

export const toRestorableSessionProfile = (
  profile: AuthSessionProfileResponse,
): RestorableSessionProfile => ({
  user: toSessionUser(profile.user),
  organization: profile.organization
    ? toCurrentSessionOrganization(profile.organization)
    : null,
  membership: profile.membership
    ? toSessionMembership(profile.membership)
    : null,
  policies: profile.policies ?? [],
})
